import type { ShowcaseVariant } from './showcase/fixtures'

import spotify from '../../../../app/assets/images/partners-round-svg/spotify.svg'
import appleMusic from '../../../../app/assets/images/partners-round-svg/itunes.svg'
import youtubeMusic from '../../../../app/assets/images/partners-round-svg/youtube-music.svg'
import amazon from '../../../../app/assets/images/partners-round-svg/amazon.svg'
import jiosaavn from '../../../../app/assets/images/partners-round-svg/saavn.svg'
import deezer from '../../../../app/assets/images/partners-round-svg/deezer.svg'

// The smart link section's picture: the page a FAN lands on, not a dashboard
// screen — so a phone, not the browser frame the other sections use.
//
// Drawn the way lnkb.ee draws it (lnkbee's ReleasePageBody + StoreRow on its
// dark preset): the artwork whole, the title and artist, a mono "Listen now"
// stamp, one row per store with its round mark and a "Play →" label. Plain
// markup, not an iframe — lnkbee is another app, and this has to render on the
// server like the rest of the marketing page.
//
// The release is the sample workspace's live one (fixtures/shared.ts), so the
// section shows the same record as the catalogue and release frames.

// lnkbee's DARK_PRESET (app/frontend/lib/theme.ts in the lnkbee repo).
const THEME = {
  background: '#14161C',
  card: '#1C1F27',
  border: '#303543',
  foreground: '#EEF0F4',
  primary: '#EAF3A6',
} as const
const MUTED = `color-mix(in srgb, ${THEME.foreground} 62%, transparent)`
const FAINT = `color-mix(in srgb, ${THEME.foreground} 48%, transparent)`
const STAMP = 'font-mono text-[10.5px] font-medium uppercase tracking-[.14em]'

const RELEASE: Record<ShowcaseVariant, { title: string; artist: string; cover: string; code: string; date: string }> = {
  artist: { title: 'Kaveri', artist: 'Nila', cover: '/artwork-styles/botanical.jpg', code: 'kaveri', date: '29 Aug 2026' },
  label: { title: 'Ninety Days', artist: 'Kavya Rao', cover: '/artwork-styles/astral.jpg', code: 'ninety-days', date: '29 Aug 2026' },
}

// Only active stores (config/stores.json); lnkbee's own words for each row.
const STORES = [
  { name: 'Spotify', logo: spotify, action: 'Play' },
  { name: 'Apple Music', logo: appleMusic, action: 'Play' },
  { name: 'YouTube Music', logo: youtubeMusic, action: 'Play' },
  { name: 'JioSaavn', logo: jiosaavn, action: 'Play' },
  { name: 'Amazon Music', logo: amazon, action: 'Play' },
  { name: 'Deezer', logo: deezer, action: 'Play' },
]

export default function SmartLinkShowcase({ variant, label }: { variant: ShowcaseVariant; label: string }) {
  const release = RELEASE[variant]
  return (
    <figure className="mx-auto w-full max-w-[360px]">
      <div
        role="img"
        aria-label={label}
        className="overflow-hidden rounded-[2.25rem] border border-white/10 bg-[#050506] p-2 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
      >
        <div aria-hidden="true" className="overflow-hidden rounded-[1.75rem]" style={{ background: THEME.background, color: THEME.foreground }}>
          <div className="flex justify-center px-4 pb-2 pt-3">
            <div className="rounded-full px-4 py-1 text-[11px]" style={{ background: THEME.card, color: MUTED }}>
              lnkb.ee/{release.code}
            </div>
          </div>

          <div className="flex flex-col items-center px-5 pb-8 pt-4" style={{ fontFamily: 'Manrope, var(--wavbee-font-sans, sans-serif)' }}>
            <img
              src={release.cover}
              alt=""
              width={600}
              height={600}
              loading="lazy"
              className="mb-5 aspect-square w-full max-w-[220px] object-cover"
              style={{ borderRadius: '1rem', boxShadow: '0 28px 60px rgba(0,0,0,.35)' }}
            />
            <div className="mb-5 w-full text-center">
              <p className="text-[20px] font-extrabold leading-[1.15] tracking-[-0.02em]" style={{ fontFamily: 'Gabarito, var(--wavbee-font-display, sans-serif)' }}>
                {release.title}
              </p>
              <p className="mt-1 text-[13px]" style={{ color: MUTED }}>{release.artist}</p>
              <p className={`mt-2 ${STAMP}`} style={{ color: FAINT }}>{release.date}</p>
            </div>

            <p className={`mb-3 ${STAMP}`} style={{ color: FAINT }}>Listen now</p>
            <ul className="flex w-full flex-col gap-2">
              {STORES.map((store) => (
                <li
                  key={store.name}
                  className="flex min-h-[48px] items-center gap-3 border px-3.5 text-[14px] font-bold"
                  style={{ background: THEME.card, borderColor: THEME.border, borderRadius: '0.75rem' }}
                >
                  <img src={store.logo} alt="" width={28} height={28} className="h-7 w-7 shrink-0 rounded-full" />
                  <span className="flex-1 truncate">{store.name}</span>
                  <span className={STAMP} style={{ color: THEME.primary }}>{store.action} →</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <figcaption className="mt-3 text-center text-xs text-white/50">Sample data</figcaption>
    </figure>
  )
}
